/**
 * The Horizon hero and its journey cards (redesign 2026-10-01).
 *
 * <p>Geometry, spacing and type follow the mockups' `css/tokens.css`; every colour resolves
 * through a token. The `--ae-hero-*` scope below reads the palette's hero leaves first and
 * falls back to the Horizon dark values, the same contract as `BASE_CSS`: a payload with no
 * tokens still renders the approved pre-dawn hero, and the merged Day palette retunes the
 * sky, the panel fills AND their opacities (`css/skin-light.css` turns the translucent navy
 * panels into near-opaque light slate — the owner rejected dark panel squares on a light
 * sky), with no change here.
 */
export const SHOP_INTRO_CSS = `
.ae-intro{
  --ae-hero-sky-top:var(--hero-sky-top,#0a1424);
  --ae-hero-sky-mid:var(--hero-sky-mid,#0c1930);
  --ae-hero-sky-low:var(--hero-sky-low,#11253f);
  --ae-hero-glow:var(--hero-glow,#ffb547);
  --ae-hero-ember:var(--hero-ember,#f47832);
  --ae-hero-core:var(--hero-core,#ffd9a0);
  --ae-hero-panel-hi:var(--hero-panel-hi,#1b2d4a);
  --ae-hero-panel-hi-a:var(--hero-panel-hi-a,0.65);
  --ae-hero-panel-lo:var(--hero-panel-lo,#0d192b);
  --ae-hero-panel-lo-a:var(--hero-panel-lo-a,0.25);
  --ae-hero-stroke:var(--hero-stroke,#9fb0c8);
  --ae-hero-stroke-a1:var(--hero-stroke-a1,0.18);
  --ae-hero-stroke-a2:var(--hero-stroke-a2,0.25);
  --ae-hero-stroke-a3:var(--hero-stroke-a3,0.10);
  --ae-hero-sheen:var(--hero-sheen,#ffffff);
  --ae-hero-ink:var(--color-text-inverse,#e8eef7);
  --ae-hero-muted:var(--color-text-inverse-muted,#9fb0c8);
  --ae-hero-line:var(--color-border-inverse,rgba(232,238,247,0.12));
  --ae-hero-line-strong:var(--color-border-inverse-strong,rgba(232,238,247,0.28));
  --ae-hero-accent:var(--color-accent-on-band,#ffb547);
  --ae-hero-btn:var(--color-text-on-accent,#10233e);
  --ae-hero-btn-hover:var(--color-brand-700,#1b2d4a);
  --ae-glass-fill:var(--glass-fill,rgba(255,255,255,0.07));
  --ae-glass-line:var(--glass-line,rgba(255,255,255,0.14));
  --ae-glass-fallback:var(--glass-fallback,#142741);
  --ae-glass-shadow:var(--shadow-glow,0 0 48px rgba(255,181,71,0.18)),var(--shadow-lift,0 24px 64px rgba(6,12,22,0.45));
}

/* ── Hero — dawn light on glass ─────────────────────────────────────────── */
.ae-hero{position:relative;isolation:isolate;overflow:hidden;background:var(--ae-hero-sky-top);color:var(--ae-hero-ink)}
.ae-hero :focus-visible{outline-color:var(--ae-hero-accent)}
.ae-dawn{position:absolute;inset:0;width:100%;height:100%;z-index:-1}
.ae-dawn__sky-top{stop-color:var(--ae-hero-sky-top)}
.ae-dawn__sky-mid{stop-color:var(--ae-hero-sky-mid)}
.ae-dawn__sky-low{stop-color:var(--ae-hero-sky-low)}
.ae-dawn__glow{stop-color:var(--ae-hero-glow)}
.ae-dawn__ember{stop-color:var(--ae-hero-ember)}
.ae-dawn__core{stop-color:var(--ae-hero-core)}
.ae-dawn__sheen{stop-color:var(--ae-hero-sheen)}
.ae-dawn__panel-hi{stop-color:var(--ae-hero-panel-hi);stop-opacity:var(--ae-hero-panel-hi-a)}
.ae-dawn__panel-lo{stop-color:var(--ae-hero-panel-lo);stop-opacity:var(--ae-hero-panel-lo-a)}
.ae-dawn__array{stroke:var(--ae-hero-stroke)}
.ae-dawn__array--far{stroke-opacity:var(--ae-hero-stroke-a1)}
.ae-dawn__array--near{stroke-opacity:var(--ae-hero-stroke-a2)}
.ae-dawn__rails{stroke:var(--ae-hero-stroke);stroke-opacity:var(--ae-hero-stroke-a3)}

.ae-hero__grid{display:grid;grid-template-columns:1fr;gap:32px;align-items:center;padding-top:48px;padding-bottom:32px}
.ae-hero__copy{min-width:0}
.ae-hero__eyebrow{margin:0 0 16px;font-size:.75rem;line-height:1.3;font-weight:650;letter-spacing:.08em;text-transform:uppercase;color:var(--ae-hero-accent)}
.ae-hero__title{margin:0 0 24px;font-size:clamp(2.5rem,6vw,4.5rem);line-height:1.03;font-weight:700;letter-spacing:-.025em;color:var(--ae-hero-ink);text-wrap:balance}
.ae-hero__sub{margin:0 0 32px;max-width:34em;font-size:1.125rem;line-height:1.65;color:var(--ae-hero-muted)}
.ae-hero__ctas{display:flex;align-items:center;gap:12px 16px;flex-wrap:wrap}
.ae-hero__ctas .ae-btn{min-height:48px;padding-inline:24px}
.ae-hero__quiet{background:transparent;color:var(--ae-hero-ink);box-shadow:inset 0 0 0 1px var(--ae-hero-line-strong)}
.ae-hero__quiet:hover{color:var(--ae-hero-ink);box-shadow:inset 0 0 0 1px var(--ae-hero-muted)}
.ae-hero__chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:24px}
.ae-hero__chips a{display:inline-flex;align-items:center;min-height:38px;padding:8px 14px;border:1px solid var(--ae-hero-line-strong);border-radius:999px;color:var(--ae-hero-ink);font-size:.8125rem;font-weight:500;text-decoration:none}
.ae-hero__chips a:hover{border-color:var(--ae-hero-muted)}

/* ── Glass spotlight — solid fallback first, glass where supported ──────── */
.ae-glass{max-width:420px;width:100%;padding:24px;background:var(--ae-glass-fallback);border:1px solid var(--ae-glass-line);border-radius:16px;box-shadow:var(--ae-glass-shadow)}
@supports ((backdrop-filter:blur(12px)) or (-webkit-backdrop-filter:blur(12px))){
  .ae-glass{background:var(--ae-glass-fill);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px)}
}
.ae-glass--loading{min-height:420px}
.ae-glass__tag{display:inline-block;margin-bottom:16px;padding:4px 11px;border-radius:999px;background:var(--ae-accent);color:var(--ae-on-accent);font-size:.6875rem;font-weight:700;letter-spacing:.09em;text-transform:uppercase}
.ae-glass__media{display:grid;place-items:center;height:220px;margin-bottom:16px;padding:16px;background:var(--ae-surface);border:1px solid var(--ae-glass-line);border-radius:12px}
.ae-glass__media img{max-height:188px;max-width:100%;width:auto;object-fit:contain}
.ae-glass__media .ae-icon{width:96px;height:96px;stroke-width:1.2}
.ae-glass__name{margin:0;font-size:1.125rem;line-height:1.35;font-weight:650;letter-spacing:-.01em}
.ae-glass__name a{color:var(--ae-hero-ink);text-decoration:none}
.ae-glass__name a:hover{text-decoration:underline;text-underline-offset:4px}
.ae-glass__mono{display:block;margin:6px 0 12px;font-family:var(--font-family-mono,ui-monospace,Menlo,monospace);font-size:.8125rem;color:var(--ae-hero-muted)}
.ae-glass__specs{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 16px;padding:0;list-style:none}
.ae-glass__specs li{padding:4px 11px;border:1px solid var(--ae-hero-line-strong);border-radius:999px;color:var(--ae-hero-ink);font-size:.78rem;font-weight:550}
.ae-glass__buy{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;border-top:1px solid var(--ae-glass-line);padding-top:16px}
.ae-glass .ae-price__now{font-size:1.5rem;color:var(--ae-hero-ink)}
.ae-glass .ae-price__basis,.ae-glass .ae-price__alt{color:var(--ae-hero-muted)}
.ae-glass .ae-price--loading{background:var(--ae-hero-line)}
.ae-glass .ae-btn--brand{background:var(--ae-hero-btn)}
.ae-glass .ae-btn--brand:hover:not(:disabled){background:var(--ae-hero-btn-hover)}
.ae-glass .ae-add-status{flex-basis:100%;color:var(--ae-hero-muted)}
.ae-glass .ae-add-status a{color:var(--ae-hero-accent)}

/* ── Two paths — homeowner / trade, the mockups' path-card anatomy ──────── */
.ae-paths{display:grid;grid-template-columns:1fr;gap:24px;padding-top:clamp(24px,3vw,40px);padding-bottom:clamp(32px,4vw,64px)}
.ae-path{position:relative;overflow:hidden;display:flex;flex-direction:column;gap:12px;padding:32px;background:var(--ae-surface);border:1px solid var(--ae-border);border-radius:16px;box-shadow:var(--ae-shadow-sm);color:var(--ae-ink);text-decoration:none;transition:border-color .15s ease,box-shadow .18s ease}
.ae-path::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,var(--ae-accent),transparent)}
.ae-path:hover{border-color:var(--ae-border-strong);box-shadow:var(--ae-shadow)}
.ae-path__kicker{font-size:.75rem;line-height:1.3;font-weight:650;letter-spacing:.08em;text-transform:uppercase;color:var(--ae-accent-ink)}
.ae-path__title{margin:0;font-size:1.375rem;line-height:1.2;font-weight:680;letter-spacing:-.015em;color:var(--ae-ink)}
.ae-path__text{max-width:36em;color:var(--ae-muted)}
.ae-path__go{margin-top:4px;font-weight:650;color:var(--ae-link)}
.ae-path:hover .ae-path__go{text-decoration:underline;text-underline-offset:4px}

@media(min-width:768px){
  .ae-paths{grid-template-columns:1fr 1fr}
}
@media(min-width:1024px){
  .ae-hero__grid{grid-template-columns:1.1fr .9fr;gap:64px;padding-top:96px;padding-bottom:64px}
  .ae-glass{justify-self:end}
}
@media(max-width:640px){
  .ae-hero__grid{padding-top:32px;padding-bottom:24px;gap:24px}
  .ae-hero__sub{margin-bottom:24px;font-size:1.0625rem}
  .ae-glass{max-width:none;padding:20px}
  .ae-glass__media{height:180px}
  .ae-glass__media img{max-height:150px}
  .ae-path{padding:24px}
}
@media(prefers-reduced-motion:reduce){.ae-path{transition:none}}
`;
